import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Coord } from '@everyboard/games';
import { Ordinal } from '@everyboard/games';
import { Player, PlayerOrNone } from '@everyboard/games';
import { PlayerNumberMap } from '@everyboard/games';
import { RulesFailure } from '@everyboard/games';
import { ScoreName } from '@everyboard/games';
import { EpaminondasAttackHeuristic } from '@everyboard/games/epaminondas';
import { EpaminondasFailure } from '@everyboard/games/epaminondas';
import { EpaminondasMove } from '@everyboard/games/epaminondas';
import { EpaminondasMoveGenerator } from '@everyboard/games/epaminondas';
import { EpaminondasPhalanxSizeAndFilterMoveGenerator } from '@everyboard/games/epaminondas';
import { EpaminondasPieceThenRowDominationThenAlignmentThenRowPresenceHeuristic } from '@everyboard/games/epaminondas';
import { EpaminondasPositionalHeuristic } from '@everyboard/games/epaminondas';
import {
    EpaminondasConfig,
    EpaminondasLegalityInformation,
    EpaminondasNode,
    EpaminondasRules,
} from '@everyboard/games/epaminondas';
import { EpaminondasState } from '@everyboard/games/epaminondas';
import { MGPOptional, MGPValidation, Utils } from '@everyboard/lib';

import { Arrow } from '../../components/game-components/arrow-component/Arrow';
import { DirArrowComponent } from '../../components/game-components/arrow-component/dir-arrow.component';
import { ClickHandler } from '../../components/game-components/game-component/ClickHandler';
import { RectangularGameComponent } from '../../components/game-components/rectangular-game-component/RectangularGameComponent';

export type PossibleMove = {

    arrow: Arrow<Ordinal>;

    endingCoord: Coord;

    relatedMove: EpaminondasMove;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-epaminondas',
    templateUrl: './epaminondas.component.html',
    styleUrls: ['../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass, DirArrowComponent],
})
export class EpaminondasComponent extends RectangularGameComponent<EpaminondasRules,
                                                                   EpaminondasMove,
                                                                   EpaminondasState,
                                                                   PlayerOrNone,
                                                                   EpaminondasConfig,
                                                                   EpaminondasLegalityInformation>
{

    public NONE: PlayerOrNone = PlayerOrNone.NONE;

    // Data linked to the move attempt
    public firstPiece: MGPOptional<Coord> = MGPOptional.empty();
    public lastPiece: MGPOptional<Coord> = MGPOptional.empty();
    public possibleMoves: PossibleMove[] = [];

    // Data linked to the last move
    private moveds: Coord[] = [];
    private capturedCoords: Coord[] = [];

    public constructor() {
        super('Epaminondas');
        this.aiConfig = {
            minimax: [
                {
                    id: 'Piece > Row Domination > Alignment > Row Presence',
                    name: $localize`Piece > Row Domination > Alignment > Row Presence`,
                    heuristic: (): EpaminondasPieceThenRowDominationThenAlignmentThenRowPresenceHeuristic => {
                        return new EpaminondasPieceThenRowDominationThenAlignmentThenRowPresenceHeuristic();
                    },
                    moveGenerator: (): EpaminondasPhalanxSizeAndFilterMoveGenerator => {
                        return new EpaminondasPhalanxSizeAndFilterMoveGenerator();
                    },
                },
                {
                    id: 'Positional',
                    name: $localize`Positional`,
                    heuristic: (): EpaminondasPositionalHeuristic => new EpaminondasPositionalHeuristic(),
                    moveGenerator: (): EpaminondasPhalanxSizeAndFilterMoveGenerator => {
                        return new EpaminondasPhalanxSizeAndFilterMoveGenerator();
                    },
                },
                {
                    id: 'Attack',
                    name: $localize`Attack`,
                    heuristic: (): EpaminondasAttackHeuristic => new EpaminondasAttackHeuristic(),
                    moveGenerator: (): EpaminondasPhalanxSizeAndFilterMoveGenerator => {
                        return new EpaminondasPhalanxSizeAndFilterMoveGenerator();
                    },
                },
            ],
            mcts: [{
                id: 'default',
                name: $localize`MCTS`,
                moveGenerator: (): EpaminondasMoveGenerator => new EpaminondasMoveGenerator(),
            }],
        };
        this.encoder = EpaminondasMove.encoder;
        this.hasAsymmetricBoard = true;
    }

    protected override getScoreName(): ScoreName {
        return ScoreName.REMAINING_PIECES;
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        this.board = this.state().getCopiedBoard();
        this.scores = this.getScores();
    }

    private getScores(): MGPOptional<PlayerNumberMap> {
        const state: EpaminondasState = this.state();
        const playerMap: PlayerNumberMap = PlayerNumberMap.of(
            state.countPieceOnBoard(Player.ZERO),
            state.countPieceOnBoard(Player.ONE),
        );
        return MGPOptional.of(playerMap);
    }

    protected override async showLastMove(move: EpaminondasMove): Promise<void> {
        this.capturedCoords = [];
        let moved: Coord = move.coord;
        this.moveds = [moved];
        for (let i: number = 1; i < (move.stepSize + move.phalanxSize); i++) {
            moved = moved.getNext(move.direction, 1);
            this.moveds.push(moved);
        }
        const previousNode: EpaminondasNode = this.node().parent.get();
        const previousOpponent: Player = this.state().getPreviousOpponent();
        while (previousNode.gameState.hasPieceAt(moved, previousOpponent)) {
            this.capturedCoords.push(moved);
            moved = moved.getNext(move.direction, 1);
        }
    }

    @ClickHandler((x: number, y: number) => `#click-${ x }-${ y }`)
    public async onClick(x: number, y: number): Promise<MGPValidation> {
        if (this.firstPiece.isPresent()) {
            return this.secondClick(x, y);
        } else {
            return this.firstClick(x, y);
        }
    }

    private async firstClick(x: number, y: number): Promise<MGPValidation> {
        const opponent: Player = this.state().getCurrentOpponent();
        const player: Player = this.state().getCurrentPlayer();
        switch (this.board[y][x]) {
            case player:
                this.firstPiece = MGPOptional.of(new Coord(x, y));
                this.possibleMoves = this.getPossibleMoves();
                return MGPValidation.SUCCESS;
            case opponent:
                return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
            default:
                Utils.expectToBe(this.board[y][x], PlayerOrNone.NONE);
                return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
    }

    public override hideLastMove(): void {
        this.capturedCoords = [];
        this.moveds = [];
    }

    private getPossibleMoves(): PossibleMove[] {
        const state: EpaminondasState = this.state();
        const possibleMoves: PossibleMove[] = [];
        for (const direction of Ordinal.ORDINALS) {
            const phalanxSize: number = this.countPhalanxSize(direction);
            let coord: Coord = this.firstPiece.get().getNext(direction, phalanxSize);
            for (let stepSize: number = 1; stepSize <= phalanxSize; stepSize++) {
                const move: EpaminondasMove = new EpaminondasMove(
                    this.firstPiece.get().x,
                    this.firstPiece.get().y,
                    phalanxSize,
                    stepSize,
                    direction,
                );
                if (this.rules.isLegal(move, state).isSuccess()) {
                    const arrow: Arrow<Ordinal> = new Arrow<Ordinal>(
                        this.firstPiece.get(),
                        coord,
                        direction,
                        (c: Coord) => this.getCenterAt(c),
                    );
                    possibleMoves.push({ arrow, endingCoord: coord, relatedMove: move });
                } else {
                    break;
                }
                coord = coord.getNext(direction, 1);
            }
        }
        return possibleMoves;
    }

    public getCenterAt(coord: Coord): Coord {
        return new Coord(
            this.SPACE_SIZE * (coord.x + 0.5),
            this.SPACE_SIZE * (coord.y + 0.5),
        );
    }

    public override cancelMoveAttempt(): void {
        this.firstPiece = MGPOptional.empty();
        this.possibleMoves = [];
        this.lastPiece = MGPOptional.empty();
    }

    private async secondClick(x: number, y: number): Promise<MGPValidation> {
        const clicked: Coord = new Coord(x, y);
        const firstPiece: Coord = this.firstPiece.get();
        if (clicked.equals(firstPiece)) {
            return this.cancelMove();
        }
        const validMoves: PossibleMove[] =
            this.possibleMoves.filter((m: PossibleMove) => m.endingCoord.equals(clicked));
        if (validMoves.length > 0) {
            return this.chooseMove(validMoves[0].relatedMove);
        }
        const player: Player = this.getCurrentPlayer();
        if (this.state().getPieceAt(clicked) === player) {
            return this.firstClick(x, y);
        }
        if (clicked.isAlignedWith(firstPiece) === false) {
            return this.cancelMove(EpaminondasFailure.SQUARE_NOT_ALIGNED_WITH_PHALANX());
        }
        return this.chooseMoveFromClicks(clicked);
    }

    private chooseMoveFromClicks(clicked: Coord): Promise<MGPValidation> {
        const direction: Ordinal = this.firstPiece.get().getDirectionToward(clicked).get();
        const phalanxSize: number = this.countPhalanxSize(direction);
        const stepSize: number = this.getStepSize(clicked, phalanxSize);
        if (stepSize > phalanxSize) {
            return this.cancelMove(EpaminondasFailure.PHALANX_CANNOT_JUMP_FURTHER_THAN_ITS_SIZE(stepSize, phalanxSize));
        }
        const move: EpaminondasMove = new EpaminondasMove(this.firstPiece.get().x,
                                                          this.firstPiece.get().y,
                                                          phalanxSize,
                                                          stepSize,
                                                          direction);
        return this.chooseMove(move);
    }

    private countPhalanxSize(direction: Ordinal): number {
        let phalanxSize: number = 1;
        let coord: Coord = this.firstPiece.get().getNext(direction, 1);
        const currentPlayer: Player = this.state().getCurrentPlayer();
        while (this.state().hasPieceAt(coord, currentPlayer)) {
            phalanxSize++;
            coord = coord.getNext(direction, 1);
        }
        return phalanxSize;
    }

    private getStepSize(clicked: Coord, phalanxSize: number): number {
        // Only called if clicked is aligned with first piece
        const direction: Ordinal = this.firstPiece.get().getDirectionToward(clicked).get();
        let stepSize: number = 1;
        let coord: Coord = this.firstPiece.get().getNext(direction, phalanxSize);
        while (coord.equals(clicked) === false) {
            stepSize++;
            coord = coord.getNext(direction, 1);
        }
        return stepSize;
    }

    public getPieceClasses(x: number, y: number): string[] {
        const player: string = this.getPlayerClass(this.board[y][x]);
        const stroke: string[] = this.getPieceStrokeClasses(x, y);
        return stroke.concat([player]);
    }

    private getPieceStrokeClasses(x: number, y: number): string[] {
        // Show pieces belonging to the phalanx to move
        const coord: Coord = new Coord(x, y);
        if (this.firstPiece.equalsValue(coord)) {
            return ['selected-stroke'];
        } else {
            return [];
        }
    }

    public getRectClasses(x: number, y: number): string[] {
        const clicked: Coord = new Coord(x, y);
        if (this.capturedCoords.some((c: Coord) => c.equals(clicked))) {
            return ['captured-fill'];
        } else if (this.moveds.some((c: Coord) => c.equals(clicked))) {
            return ['moved-fill'];
        }
        return [];
    }

    public getHighlightedCoords(): Coord[] {
        if (this.interactive() === false) {
            return [];
        }
        if (this.firstPiece.isPresent()) {
            return this.possibleMoves.map((p: PossibleMove) => p.endingCoord);
        } else {
            return this.getCurrentPlayerPieces();
        }
    }

    private getCurrentPlayerPieces(): Coord[] {
        const pieces: Coord[] = [];
        const state: EpaminondasState = this.state();
        const player: Player = state.getCurrentPlayer();
        for (let y: number = 0; y < this.height(); y++) {
            for (let x: number = 0; x < this.width(); x++) {
                if (this.board[y][x] === player) {
                    pieces.push(new Coord(x, y));
                }
            }
        }
        return pieces;
    }

}
